/**
 * DeteriorationEngine — Simula el paso del tiempo clínico.
 *
 * Lee el reloj del paciente (state.simulation.patientTime) y aplica cambios
 * de trayectoria cuando se cumple cada milestone. Un paso puede ser prevenido
 * si el alumno ordenó el examen/tratamiento mitigante antes del disparo.
 *
 * Efecto final: si el alumno se demora, los vitales empeoran, el triage sube
 * y aparecen notificaciones narrativas. Si actúa a tiempo, ve feedback positivo.
 * Sin esto, el paciente era una foto estática; con esto, es un reloj corriendo.
 */

import state from '../core/state';
import { Patient } from './Patient';
import {
  DeteriorationStep,
  DeteriorationTrajectory,
  DETERIORATION_TRAJECTORIES,
} from '../education/deterioration-data';

export class DeteriorationEngine {
  private patient: Patient | null = null;
  private trajectory: DeteriorationTrajectory | null = null;
  private firedSteps: Set<string> = new Set();
  private orderedExams: Set<string> = new Set();
  private unsubscribers: Array<() => void> = [];

  start(): void {
    this.stop();
    this.unsubscribers.push(
      state.onEvent('EXAM_ORDERED', (e) => {
        if (e.type === 'EXAM_ORDERED') this.orderedExams.add(e.examId);
      })
    );
  }

  stop(): void {
    this.unsubscribers.forEach(fn => fn());
    this.unsubscribers = [];
  }

  /**
   * Activa la trayectoria de un caso. Llamar en loadPatient() después de que
   * el Patient 3D esté construido. Si el caso no tiene trayectoria definida,
   * el engine queda inactivo (no dispara nada).
   */
  setCase(patient: Patient, caseId: string): void {
    this.patient = patient;
    this.trajectory = DETERIORATION_TRAJECTORIES[caseId] ?? null;
    this.firedSteps.clear();
    this.orderedExams.clear();
  }

  clear(): void {
    this.patient = null;
    this.trajectory = null;
    this.firedSteps.clear();
    this.orderedExams.clear();
  }

  /**
   * Estado del reloj clínico para el HUD. Devuelve null si no hay trayectoria
   * activa. nextMilestone es el próximo paso no disparado (para que el HUD
   * muestre "próximo evento en 47s" y cambie de color cerca del disparo).
   */
  getClockState(): {
    minutes: number;
    totalMilestones: number;
    firedCount: number;
    nextMilestoneMin: number | null;
  } | null {
    if (!this.trajectory) return null;
    const minutes = state.simulation.patientTime / 60;
    const pending = this.trajectory.steps
      .filter(s => !this.firedSteps.has(s.id))
      .sort((a, b) => a.atMinutes - b.atMinutes);
    return {
      minutes,
      totalMilestones: this.trajectory.steps.length,
      firedCount: this.firedSteps.size,
      nextMilestoneMin: pending.length > 0 ? pending[0].atMinutes : null,
    };
  }

  hasTrajectory(): boolean {
    return this.trajectory !== null;
  }

  /**
   * Llamar en cada frame del loop principal. Chequea qué pasos deben dispararse
   * según patientTime y los ejecuta (o los marca como prevenidos).
   */
  tick(): void {
    if (!this.trajectory || !this.patient) return;
    if (state.simulation.isPaused) return;

    const minutes = state.simulation.patientTime / 60;

    for (const step of this.trajectory.steps) {
      if (this.firedSteps.has(step.id)) continue;
      if (minutes < step.atMinutes) continue;

      this.firedSteps.add(step.id);
      this.executeStep(step);
    }
  }

  /**
   * Un paso está prevenido si el alumno ordenó alguno de los exams/tratamientos
   * listados en preventedByExamIds antes de que el timer llegara al disparo.
   */
  private executeStep(step: DeteriorationStep): void {
    const prevented = step.preventedByExamIds?.some(id => this.orderedExams.has(id)) ?? false;

    state.emit({
      type: 'DETERIORATION_STEP',
      stepId: step.id,
      atMinutes: step.atMinutes,
      prevented,
      narrative: prevented ? (step.preventedNarrative ?? 'Paso prevenido.') : step.narrative,
    });

    if (prevented) {
      state.addNotification({
        type: 'success',
        title: `t+${step.atMinutes}min — Prevenido`,
        message: step.preventedNarrative ?? 'Tu intervención evitó el deterioro.',
        duration: 6000,
      });
      return;
    }

    // Mutar el nodo. El Patient actualiza label y re-chequea status crítico.
    const node = this.patient!.getNode(step.nodeId);
    if (!node) return;

    const oldValue: unknown = node.data.valueQuantity?.value ?? node.data.valueString;
    this.patient!.updateNodeValue(step.nodeId, step.newValue);

    if (step.severity) {
      node.data.severity = step.severity;
    }
    node.pulse();

    // Propagación al bus: ReasoningEngine recalcula patrones + triage;
    // DifferentialPanel reajusta probabilidades con el nuevo valor.
    state.emit({
      type: 'VITAL_CHANGED',
      nodeId: step.nodeId,
      oldValue,
      newValue: step.newValue,
      narrative: step.narrative,
      trend: 'worsening',
    });
    state.emitFindingRevealed(step.nodeId, 'timeline');

    state.addNotification({
      type: step.severity === 'life-threatening' ? 'critical' : 'warning',
      title: `t+${step.atMinutes}min — Deterioro`,
      message: step.narrative,
      duration: step.severity === 'life-threatening' ? 0 : 8000,
    });
  }
}

export default DeteriorationEngine;
